// src/components/stages/PreConsultStage.tsx
//
// Rebuilt on the shared layout primitives per
// docs/HMS_V4_UI_MODERNIZATION_ASSESSMENT.md item 5 / the Pre-Consult
// wireframe: full-width, lettered SectionCards instead of a single 680px
// column, the auto-summary promoted to a persistent strip instead of
// rendered last, and a sticky StageActionBar instead of a bare terminal
// button. All data logic — template resolution, voice fill, the scoring
// call, the submit payload — is unchanged from the prior version; only the
// render tree and layout changed.
import { useState, useEffect, useMemo, useRef } from 'react';
import { getAilmentTemplates, getAssessmentSummary, uploadPatientDocument, getPatientTimeline, generatePatientTimeline } from '../../api';
import { loadConfig, getActiveModel } from '../../aiConfig';
import VoiceMicButton from '../VoiceMicButton';
import VoiceFormFiller, { type VoiceField } from '../VoiceFormFiller';
import PatientHistoryPanel, { type HistoryKind } from '../PatientHistoryPanel';
import type { AssessmentSummary } from '../AssessmentSummaryCard';
import MobileUploadQRModal from '../MobileUploadQRModal';
import { isFieldVisible } from '../../utils/conditions';
import { RISK_LEVELS, ALL_TRIAGE_FIELD_KEYS, type RiskLevel, type TriageFieldKey } from './TriageFieldsEditor';
import {
  AilmentAssessmentEditor, letterAt, fieldsAnsweredCount, statusFromCount,
  type FieldValue,
} from './AilmentAssessmentEditor';
import { PreConsultPatientHero } from './preconsult/PreConsultPatientHero';
import { TriageChipRow } from './preconsult/TriageChipRow';
import { AssessmentSummaryRail } from './preconsult/AssessmentSummaryRail';
import { SectionCard } from '../ui/SectionCard';
import { StageActionBar } from '../ui/StageActionBar';
import { StageLayout } from '../ui/StageLayout';
import { Button } from '../ui/Button';
import { dueChecklistStatus, isDue } from '../../lib/dueChecklist';
import { normaliseTemplate, templateShape, type AilmentTemplate, type NormalisedSection } from '../../lib/assessmentTemplate';
import type { StageProps } from '../../types/visit';

const GENERIC_TRIAGE_KEYS = ['risk_level', 'pain_scale', 'allergies', 'current_meds', 'nurse_notes', 'additional_vitals'];
const isEmptyValue = (v: unknown) => v === '' || v == null || (Array.isArray(v) && v.length === 0) || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0);

const HISTORY_KINDS: HistoryKind[] = ['diagnoses', 'procedures', 'medications'];
type HistoryStatus = null | 'fresh' | 'stale' | 'populating' | 'error';

const UPLOAD_CATS = ['Discharge Summary', 'Lab Report', 'X-Ray / Scan', 'Prescription', 'ECG / Echo', 'Other'];

// Patients often arrive with prior-hospital paperwork (old discharge
// summaries, scans, referral letters) in hand right at this stage — this is
// the natural point to capture it into the patient's record (same upload
// pipeline/OCR as the Documents tab), rather than relying on it being added
// later or not at all.
function PriorHistoryUpload({ uhid }: { uhid: string | undefined }) {
  const [cat, setCat] = useState(UPLOAD_CATS[0]!);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploaded, setUploaded] = useState<Array<{ id: string | number; original_name: string; doc_category: string }>>([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | null | undefined) => {
    if (!file || !uhid) return;
    setUploading(true); setError('');
    try {
      const doc = await uploadPatientDocument(uhid, file, cat);
      setUploaded(p => [...p, doc]);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setUploading(false);
  };

  // "Scan to upload" received documents don't come back through this
  // component (the phone uploads straight to the backend) — just note that
  // at least one arrived; the full list is always visible in Documents.
  const handleQrReceived = async () => {
    setUploaded(p => [...p, { id: `qr-${Date.now()}`, original_name: 'Received from phone', doc_category: cat }]);
  };

  // Collapsed by default — a sometimes-task, not a step every visit needs
  // (see the wireframe's own "collapsed by default" note for this block).
  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="mb-3.5 flex w-full items-center gap-2 rounded-[var(--radius)] border border-dashed border-line px-3.5 py-2.5 text-left text-[12.5px] text-ink-muted hover:border-teal hover:text-teal"
      >
        📎 Previous hospital records {uploaded.length > 0 ? `— ${uploaded.length} added` : '— click to upload or scan'}
      </button>
    );
  }

  return (
    <div className="mb-3.5 rounded-[var(--radius)] border border-line bg-card p-3.5">
      <div className="mb-1.5 flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-wide text-ink-muted">📎 Previous hospital records</div>
        <button type="button" className="text-[11px] text-ink-muted hover:text-ink" onClick={() => setExpanded(false)}>▲ Collapse</button>
      </div>
      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>
        If the patient brought old reports, discharge summaries, or prescriptions from elsewhere, upload them now so they're saved to the patient's Documents.
      </div>

      {/* Category chip row — replaces the plain <select>. UPLOAD_CATS unchanged. */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {UPLOAD_CATS.map(c => (
          <button
            key={c}
            type="button"
            onClick={() => setCat(c)}
            className={
              c === cat
                ? 'rounded-full border border-teal bg-accent px-2.5 py-1 text-[11px] font-semibold text-teal'
                : 'rounded-full border border-line px-2.5 py-1 text-[11px] font-semibold text-ink-muted hover:border-teal hover:text-teal'
            }
          >
            {c}
          </button>
        ))}
      </div>

      {/* Dashed dropzone — click or drag a file; "scan to upload" stays a
          separate action inside it rather than a sibling button. */}
      <div
        role="button"
        tabIndex={uhid ? 0 : -1}
        onClick={() => uhid && fileRef.current?.click()}
        onKeyDown={e => { if (uhid && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); fileRef.current?.click(); } }}
        onDragOver={e => { e.preventDefault(); if (uhid) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault();
          setDragOver(false);
          if (uhid) handleFile(e.dataTransfer.files?.[0]);
        }}
        className={
          'flex flex-col items-center gap-1 rounded-[9px] border-2 border-dashed px-6 py-3.5 text-center text-[11.5px] text-ink-muted ' +
          (uhid ? 'cursor-pointer ' : 'cursor-not-allowed opacity-60 ') +
          (dragOver ? 'border-teal bg-accent' : 'border-control-line')
        }
      >
        <span>⬆ {uploading ? 'Uploading…' : <b className="font-semibold text-ink">Upload document</b>}</span>
        <span>
          or{' '}
          <button
            type="button"
            className="font-semibold text-teal underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-ink-muted disabled:no-underline"
            disabled={!uhid}
            onClick={e => { e.stopPropagation(); setQrOpen(true); }}
          >
            scan to upload
          </button>
        </span>
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.tiff" style={{ display: 'none' }}
        onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />

      {error && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 6 }}>⚠ {error}</div>}
      {uploaded.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {uploaded.map(d => (
            <div key={d.id} style={{ fontSize: 12, color: '#065F46' }}>
              ✓ {d.original_name} <span style={{ color: '#9CA3AF' }}>({d.doc_category})</span>
            </div>
          ))}
        </div>
      )}
      {qrOpen && uhid && (
        <MobileUploadQRModal uhid={uhid} category={cat} onClose={() => setQrOpen(false)} onReceived={handleQrReceived} />
      )}
    </div>
  );
}

interface VitalsSnapshot {
  bp_sys?: string | number; bp_dia?: string | number; pulse?: string | number;
  temp?: string | number; spo2?: string | number; weight?: string | number;
  height?: string | number; bmi?: string | number; bloodGroup?: string;
  symptoms?: string; selected_module?: string;
}

interface TriageState {
  risk_level: RiskLevel;
  pain_scale: string;
  allergies: string;
  current_meds: string;
  additional_vitals: string;
  nurse_notes: string;
}

interface PreConsultStageProps extends Pick<StageProps, 'visit' | 'patient' | 'completeStage' | 'loading' | 'reportScreeningsDue'> {
  /** See VitalsStage.tsx's own doc comment on the same prop — set only when
   * StageEditModal.tsx is reusing this component to amend an already-
   * completed Pre-Consult stage. The exact stage_data it was completed
   * with, seeding the triage fields and the ailment template's own answers
   * instead of starting blank. */
  amendFrom?: Record<string, any> | null;
  /** See VitalsStage.tsx's own doc comment on the same prop — StageEditModal's
   * default, pre-"Edit" state. Renders this exact form, pre-filled from
   * `amendFrom`, wrapped in a disabled `<fieldset>` instead of a separate
   * summary layout. */
  readOnly?: boolean;
}

export default function PreConsultStage({ visit, patient, completeStage, loading, reportScreeningsDue, amendFrom, readOnly }: PreConsultStageProps) {
  const [templates, setTemplates]     = useState<AilmentTemplate[] | null>(null);
  const [templateId, setTemplateId]   = useState<number | null | undefined>(undefined); // undefined = not chosen yet; null = "General / other"
  const [answers, setAnswers]         = useState<Record<string, FieldValue>>(() => amendFrom?.template_answers ?? {});
  const [summary, setSummary]         = useState<AssessmentSummary | null>(null);
  const [triage, setTriage] = useState<TriageState>(() => amendFrom ? {
    risk_level: amendFrom.risk_level || 'Low', pain_scale: amendFrom.pain_scale || '',
    allergies: amendFrom.allergies || '', current_meds: amendFrom.current_meds || '',
    additional_vitals: amendFrom.additional_vitals || '', nurse_notes: amendFrom.nurse_notes || '',
  } : {
    risk_level: 'Low', pain_scale: '', allergies: '', current_meds: '',
    additional_vitals: '', nurse_notes: '',
  });
  const set = (k: keyof TriageState, v: string) => setTriage(p=>({...p,[k]:v} as TriageState));
  const [historyStatus, setHistoryStatus] = useState<HistoryStatus>(null); // null=checking | 'fresh' | 'stale' | 'populating' | 'error'
  const [historyRefresh, setHistoryRefresh] = useState(0);

  // Longitudinal history / past procedures / current drugs (moved here from
  // Vitals, KMC #27/#28): refer to what's already in the patient record if
  // it's populated and current, otherwise offer to pull it via AI (from
  // visits, consultations, vitals and uploaded documents — see
  // generatePatientTimeline / seedStructuredHistory). Patient-level, not
  // stage_data, so nothing here goes into the submit payload.
  useEffect(() => {
    if (!patient?.uhid) return;
    setHistoryStatus(null);
    getPatientTimeline(patient.uhid)
      .then((d: { events?: unknown; stale?: boolean }) => setHistoryStatus(d.events && !d.stale ? 'fresh' : 'stale'))
      .catch(() => setHistoryStatus('stale'));
  }, [patient?.uhid]);

  const populateHistory = async () => {
    if (!patient?.uhid) return;
    setHistoryStatus('populating');
    try {
      const aiCfg = loadConfig();
      await generatePatientTimeline(patient.uhid, aiCfg.provider, getActiveModel(aiCfg));
      setHistoryStatus('fresh');
      setHistoryRefresh(n => n + 1);
    } catch {
      setHistoryStatus('error');
    }
  };

  // The nurse already decided which Assessment Module (if any) applies back
  // in the Vitals stage — read that decision so this screen goes straight to
  // the right checklist instead of asking again. Older visits (started
  // before this existed) won't have selected_module set; fall back to the
  // manual picker below for those.
  const vitalsData: VitalsSnapshot | undefined = visit?.stages?.find(s => s.stage === 'vitals')?.stage_data ?? undefined;
  const selectedModuleKey = vitalsData?.selected_module;

  // A starting point for "Additional observations" when there's no
  // ailment-specific checklist to hold it instead — never clobbers something
  // the nurse already typed here (same non-clobbering pattern VitalsStage.jsx
  // itself uses to pre-fill from the patient record).
  useEffect(() => {
    if (vitalsData?.symptoms) {
      setTriage(p => ({ ...p, additional_vitals: p.additional_vitals || vitalsData.symptoms || '' }));
    }
  }, [vitalsData?.symptoms]);

  useEffect(() => {
    getAilmentTemplates().then((list: AilmentTemplate[]) => {
      const active = list.filter(t => t.active);
      setTemplates(active);
      if (selectedModuleKey && selectedModuleKey !== 'none') {
        if (selectedModuleKey === 'other') setTemplateId(null);
        else {
          const match = active.find(t => t.module_key === selectedModuleKey);
          setTemplateId(match ? match.id : null);
        }
      }
    }).catch(() => setTemplates([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedTemplate = templateId ? templates?.find(t => t.id === templateId) ?? null : null;

  // Every read of the template's structure below goes through this, never
  // through fields_config directly — see normaliseTemplate's own note on why.
  const templateSections: NormalisedSection[] = useMemo(
    () => (selectedTemplate ? normaliseTemplate(selectedTemplate) : []),
    [selectedTemplate]
  );

  // Fields whose visible_if condition (if any) is currently met, given the
  // answers so far — a field hidden by an earlier answer is excluded from
  // scoring/summary/submit too, not just from rendering.
  const visibleFieldKeys = new Set(
    templateSections.flatMap(sec => sec.fields.filter(f => isFieldVisible(f, answers)).map(f => f.key))
  );
  const visibleAnswers = Object.fromEntries(Object.entries(answers).filter(([k]) => visibleFieldKeys.has(k)));

  // Live auto-summary (per-field colors, due-checklist status, overall
  // composite risk) — recomputed as the nurse/PA fills the checklist.
  useEffect(() => {
    if (!selectedTemplate) { setSummary(null); return; }
    const hasAnyAnswer = Object.values(visibleAnswers).some(v => !(v === '' || v == null || (Array.isArray(v) && !v.length)));
    if (!hasAnyAnswer) { setSummary(null); return; }
    const t = setTimeout(() => {
      getAssessmentSummary(selectedTemplate.id, visibleAnswers).then(setSummary).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [selectedTemplate, answers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Screenings currently due, over every due-checklist field this template
  // declares. Computed from the same shared rule the checklist cards use
  // (lib/dueChecklist.ts) rather than from `summary`, so the banner badge is
  // right from the moment the template loads — `summary` stays null until at
  // least one field has an answer.
  const screeningsDue = selectedTemplate
    ? templateSections
        .flatMap(sec => sec.fields)
        .filter(f => f.type === 'due-checklist')
        .reduce((count, field) => {
          const state = answers[field.key] as Record<string, { last_done?: string }> | undefined;
          return count + field.items.filter(item => isDue(dueChecklistStatus(state?.[item.label]?.last_done, item.interval_months))).length;
        }, 0)
    : undefined;

  // Publish it to the PatientBanner, which is mounted above this stage in
  // VisitWorkflow. VisitWorkflow clears the value on any stage change, so
  // this only has to keep it current while Pre-Consult is on screen.
  useEffect(() => {
    reportScreeningsDue?.(screeningsDue);
  }, [screeningsDue, reportScreeningsDue]);

  const voiceFields: VoiceField[] = [
    { key:'risk_level', label:'Risk level', type:'radio', options: [...RISK_LEVELS] },
    { key:'pain_scale', label:'Pain scale 0-10', type:'number' },
    { key:'allergies', label:'Known allergies', type:'text' },
    { key:'current_meds', label:'Current medications', type:'text' },
    ...(selectedTemplate
      ? templateSections.flatMap(sec => sec.fields.filter(f => isFieldVisible(f, answers)).map((field): VoiceField => ({
          key: field.key, label: field.label || sec.label || field.key, type: field.type,
          // Sub-labels for a vitals row double as its options; a due-checklist's
          // item labels (the screening names) do the same — the AI needs to
          // know what's being screened for in order to attach a spoken date to
          // the right one. This used to be `undefined` for due-checklist on
          // the theory that "its answers are dates, not options", which instead
          // left the AI extracting one bare string for the whole checklist with
          // no idea which screening it belonged to — exactly why dictated dates
          // never made it onto the right row.
          options: field.options.length ? field.options : field.items.map(i => i.label),
          // choice-matrix: each row is its own subject with its own allowed
          // options — a flat options[] can't express that two rows of the
          // same matrix have different scales (see the field-types spec §2's
          // Voice fill section).
          rows: field.type === 'choice-matrix'
            ? field.rows.map(row => ({ key: row.key, label: row.label, options: field.columns.map(col => row.cells[col.value]?.option).filter((o): o is string => !!o) }))
            : undefined,
        })))
      : [{ key:'additional_vitals', label:'Additional observations', type:'text' } as VoiceField]),
    { key:'nurse_notes', label:'Nurse notes', type:'text' },
  ];

  // due-checklist fields' extracted value comes back keyed by item label (see
  // backend/routes/ai.js's describeField, same object shape as `vitals`) and
  // needs converting to this field's own {label: {last_done}} answer shape —
  // merged onto whatever was already answered rather than replacing it, since
  // one spoken passage naming two screenings should never blank out a third
  // one a nurse already dated by hand or in an earlier recording.
  const dueChecklistKeys = new Set(voiceFields.filter(f => f.type === 'due-checklist').map(f => f.key));
  const matrixKeys = new Set(voiceFields.filter(f => f.type === 'choice-matrix').map(f => f.key));

  const applyVoiceFields = (extracted: Record<string, string>) => {
    const triageUpdates: Partial<TriageState> = {};
    const answerUpdates: Record<string, FieldValue> = {};
    Object.entries(extracted).forEach(([key, value]) => {
      if (isEmptyValue(value)) return;
      if ((GENERIC_TRIAGE_KEYS as string[]).includes(key)) {
        (triageUpdates as Record<string, string>)[key] = value;
      } else if (matrixKeys.has(key)) {
        // Merge, never replace — a passage naming two rows must not blank a
        // third the nurse already answered by hand (same principle as
        // due-checklist below; the field-types spec's Voice fill section
        // calls out this exact failure mode). Guarded the same way against
        // an already-corrupted stored value.
        const existingRaw = answers[key];
        const existing = (existingRaw && typeof existingRaw === 'object' && !Array.isArray(existingRaw))
          ? existingRaw as Record<string, string>
          : {};
        const merged = { ...existing };
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          Object.entries(value as unknown as Record<string, string>).forEach(([rowKey, option]) => {
            if (option) merged[rowKey] = option;
          });
        }
        answerUpdates[key] = merged as FieldValue;
      } else if (dueChecklistKeys.has(key)) {
        // `value` is normally an object here at runtime, not a string — see
        // VoiceFormFiller.tsx's own note on why the prop type stays loose.
        // Guarded anyway on both sides of the spread: the server now
        // validates this shape (utils/extractedFields.js), but `existing`
        // spreads whatever is ALREADY stored, and an already-corrupted
        // stored value (a plain string, from before that validation
        // existed) must never be spread again — `{...aString}` silently
        // turns into character-indexed keys, which is exactly how this got
        // found (2026-09-16, see extractedFields.js's own header comment).
        const existingRaw = answers[key];
        const existing = (existingRaw && typeof existingRaw === 'object' && !Array.isArray(existingRaw))
          ? existingRaw as Record<string, { last_done?: string }>
          : {};
        const merged = { ...existing };
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          Object.entries(value as unknown as Record<string, string>).forEach(([label, dateStr]) => {
            if (dateStr) merged[label] = { last_done: dateStr };
          });
        }
        answerUpdates[key] = merged as FieldValue;
      } else {
        answerUpdates[key] = value;
      }
    });
    if (Object.keys(triageUpdates).length) setTriage(p => ({ ...p, ...triageUpdates }));
    if (Object.keys(answerUpdates).length) setAnswers(p => ({ ...p, ...answerUpdates }));
  };

  const submit = () => {
    completeStage('pre_consult', {
      ...triage,
      ailment: selectedTemplate ? selectedTemplate.name : 'General',
      ailment_template_id: selectedTemplate ? selectedTemplate.id : null,
      template_answers: selectedTemplate ? visibleAnswers : undefined,
      template_summary: selectedTemplate ? summary : undefined,
    });
  };

  if (templates === null) {
    return <div className="card fade-in" style={{ maxWidth:620, textAlign:'center', padding:40, color:'#6B7280' }}>Loading pre-consult templates…</div>;
  }

  // Step 1: nurse picks an ailment category before the checklist appears —
  // only reached when Vitals didn't record a module decision (older visits
  // predating that field). Normally selectedModuleKey already resolved
  // templateId above and this screen is skipped entirely.
  if (templateId === undefined) {
    return (
      <div className="card fade-in" style={{ maxWidth: 620 }}>
        <div style={{ fontSize:20, fontWeight:700, marginBottom:6 }}>🩺 Pre-consultation triage</div>
        <div style={{ fontSize:13, color:'#6B7280', marginBottom:20 }}>What is the patient's reported ailment?</div>

        <PreConsultPatientHero
          patient={patient}
          vitals={vitalsData}
          riskLevel={triage.risk_level}
          onRiskLevelChange={v => set('risk_level', v)}
          painScale={triage.pain_scale}
          onPainScaleChange={v => set('pain_scale', v)}
          showRiskLevel={false}
          showPainScale={false}
        />

        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {templates.map(t => (
            <div key={t.id} className="card" style={{ padding:'12px 14px', cursor:'pointer', border:'1px solid #E5E7EB' }} onClick={()=>{ setTemplateId(t.id); setAnswers({}); }}>
              <div style={{ fontWeight:600, fontSize:14 }}>{t.name}</div>
              <div style={{ fontSize:11.5, color:'#9CA3AF', marginTop:2 }}>{templateShape(t)}</div>
            </div>
          ))}
          <div className="card" style={{ padding:'12px 14px', cursor:'pointer', border:'1px dashed #D1D5DB' }} onClick={()=>{ setTemplateId(null); setAnswers({}); }}>
            <div style={{ fontWeight:600, fontSize:14, color:'#6B7280' }}>General / other</div>
            <div style={{ fontSize:11.5, color:'#9CA3AF', marginTop:2 }}>No ailment-specific checklist — record general triage notes only</div>
          </div>
        </div>

        <PriorHistoryUpload uhid={patient?.uhid} />
      </div>
    );
  }

  // ── Section list, in display order — the template/no-template case's own
  // sections, lettered from "A" (Triage no longer claims its own letter; its
  // fields live in the hero and TriageChipRow above the grid instead).
  //
  // `enabledTriageFields` — which of the four Triage fields this ailment
  // template actually wants (null/no template = all four). risk_level is
  // deliberately excluded from the countable set below even when enabled:
  // it always starts at a real value ('Low'), so counting it as "answered"
  // would never distinguish a considered choice from the untouched default.
  const enabledTriageFields = new Set(selectedTemplate?.triage_fields ?? ALL_TRIAGE_FIELD_KEYS);
  const triageCountableFields = (['pain_scale', 'allergies', 'current_meds'] as const).filter(k => enabledTriageFields.has(k));
  const triageAnsweredCount = triageCountableFields.filter(k => !isEmptyValue(triage[k])).length;
  const triageStatus = statusFromCount(triageAnsweredCount, triageCountableFields.length);

  const dynamicSections = templateSections
    .map(sec => ({ sec, visibleFields: sec.fields.filter(f => isFieldVisible(f, answers)) }))
    .filter(({ visibleFields }) => visibleFields.length > 0);

  // "Triage" no longer has its own lettered card, but its fields (in the hero
  // and TriageChipRow) still count toward overall readiness — 1 conceptual
  // section, same as before this redesign, just not one with a letter badge.
  const totalLetteredSections = 1 /* Triage */ + (selectedTemplate ? dynamicSections.length : 1) /* dynamic, or Additional observations */;
  const completedLetteredSections =
    (triageStatus === 'complete' ? 1 : 0) +
    (selectedTemplate
      ? dynamicSections.filter(({ visibleFields }) => {
          const { answered, total } = fieldsAnsweredCount(visibleFields, answers);
          return statusFromCount(answered, total) === 'complete';
        }).length
      : (isEmptyValue(triage.additional_vitals) ? 0 : 1));
  const allSectionsComplete = completedLetteredSections === totalLetteredSections;

  // Diagnoses/procedures/medications are configured per template through the
  // existing `history-table` field type (Admin → Ailment templates). A kind
  // the template places in one of its own sections renders there; every other
  // kind renders in the Patient history card below, so a template that never
  // mentions history (or General / other) still captures all three — this
  // stage is where Vitals used to capture them unconditionally.
  const templateHistoryKinds = new Set(
    dynamicSections.flatMap(({ visibleFields }) => visibleFields.filter(f => f.type === 'history-table').map(f => f.history_source))
  );
  const cardHistoryKinds = HISTORY_KINDS.filter(k => !templateHistoryKinds.has(k));

  return (
    <div className="fade-in mx-auto flex w-full max-w-[1560px] flex-col gap-3.5">
      {/* See VitalsStage.tsx's own comment on why `contents` — the fieldset
          generates no box of its own, so disabling doesn't disturb the
          StageLayout grid below it. */}
      <fieldset disabled={readOnly} className="contents border-0 p-0 m-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div style={{ fontSize:20, fontWeight:700 }}>🩺 Pre-consultation triage</div>
          <div style={{ fontSize:13, color:'#6B7280', marginTop:2 }}>{selectedTemplate ? selectedTemplate.name : 'General'} · Nurse assessment before doctor consultation</div>
        </div>
        {/* Switching to a different ailment template mid-correction would
            mean scoring/summarising against a template the visit was never
            actually assessed under — a correction fixes an answer, it
            doesn't re-run the assessment against something else. */}
        {!selectedModuleKey && !amendFrom && (
          <button className="btn-ghost" style={{ fontSize:12, padding:'5px 10px' }} onClick={()=>setTemplateId(undefined)}>← Change ailment</button>
        )}
      </div>

      <PreConsultPatientHero
        patient={patient}
        vitals={vitalsData}
        riskLevel={triage.risk_level}
        onRiskLevelChange={v => set('risk_level', v)}
        painScale={triage.pain_scale}
        onPainScaleChange={v => set('pain_scale', v)}
        showRiskLevel={enabledTriageFields.has('risk_level')}
        showPainScale={enabledTriageFields.has('pain_scale')}
        disabled={readOnly}
      />

      {/* Known allergies / Current medications / Nurse notes — replaces the
          old "Triage" SectionCard (now down to just these two fields, since
          Risk level/Pain scale moved into the hero above) and the separate
          standalone Nurse notes card. Same triage state either way. */}
      <TriageChipRow
        value={{ allergies: triage.allergies, current_meds: triage.current_meds, nurse_notes: triage.nurse_notes }}
        onChange={set}
        enabledFields={selectedTemplate?.triage_fields as TriageFieldKey[] | null | undefined}
        disabled={readOnly}
      />

      <VoiceFormFiller fields={voiceFields} onFields={applyVoiceFields} label="🎙 Fill this triage by voice" />

      {/* Section grid — full width, up to three columns, from the shared
          StageLayout primitive rather than a grid re-chosen per stage. The
          template's own sections (or, with no template, a single Additional
          observations card) start at "A" — Triage no longer claims a letter
          of its own, matching the target mockup. */}
      <StageLayout variant="three-up">
        {selectedTemplate ? (
          <AilmentAssessmentEditor
            templateId={selectedTemplate.id}
            templateSections={templateSections}
            answers={answers}
            onChange={(key, v) => setAnswers(a => ({ ...a, [key]: v }))}
            patientUhid={patient?.uhid}
            letterOffset={0}
            disabled={readOnly}
            historyRefreshToken={historyRefresh}
          />
        ) : (
          <SectionCard index={letterAt(0)} title="Additional observations" status={statusFromCount(isEmptyValue(triage.additional_vitals) ? 0 : 1, 1)}>
            <label className="lbl" style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span>Observations</span>
              <VoiceMicButton onText={t => set('additional_vitals', (triage.additional_vitals ? triage.additional_vitals + ' ' : '') + t)} />
            </label>
            <textarea style={{resize:'vertical',height:96}} value={triage.additional_vitals} onChange={e=>set('additional_vitals',e.target.value)} />
          </SectionCard>
        )}
      </StageLayout>

      {/* Unlettered, like Vitals' old card it replaces — reference material
          on the patient record, not answers to this assessment, so it
          carries no completion badge and doesn't count toward readiness.
          Full width since each embedded panel can run long. */}
      {patient?.uhid && (
        <SectionCard title="Patient history">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            {historyStatus === 'fresh' && <span style={{ fontSize:11, color:'#059669' }}>✓ Up to date</span>}
            {(historyStatus === 'stale' || historyStatus === 'error') && (
              <button className="btn-ghost" style={{ fontSize:11, padding:'3px 10px' }} onClick={populateHistory}>
                🤖 Pull from patient record via AI
              </button>
            )}
            {historyStatus === 'populating' && <span style={{ fontSize:11, color:'#6B7280' }}>⏳ Reading records & documents…</span>}
            {historyStatus === 'error' && <span style={{ fontSize:11, color:'#DC2626' }}> — try again</span>}
          </div>
          {cardHistoryKinds.map(kind => (
            <PatientHistoryPanel key={kind} kind={kind} uhid={patient.uhid} compact refreshToken={historyRefresh} />
          ))}
          {cardHistoryKinds.length === 0 && (
            <div style={{ fontSize:12, color:'#6B7280' }}>This template records the patient's history in its own sections above.</div>
          )}
        </SectionCard>
      )}

      <PriorHistoryUpload uhid={patient?.uhid} />

      {selectedTemplate && <AssessmentSummaryRail summary={summary} title={`Automatic ${selectedTemplate.name} summary`} sticky={!amendFrom} />}

      <StageActionBar
        readiness={readOnly
          ? 'Viewing a previously recorded assessment — click Edit to correct it'
          : amendFrom
          ? 'Correcting a previously recorded assessment'
          : allSectionsComplete
            ? '✓ All required sections completed'
            : `${totalLetteredSections - completedLetteredSections} section${totalLetteredSections - completedLetteredSections === 1 ? '' : 's'} still need attention`}
        readinessTone={readOnly || amendFrom ? 'neutral' : allSectionsComplete ? 'success' : 'warn'}
      >
        <Button onClick={submit} disabled={loading}>
          {loading ? 'Saving…' : amendFrom ? '💾 Save correction' : 'Complete triage & queue patient →'}
        </Button>
      </StageActionBar>
      </fieldset>
    </div>
  );
}
